import { useId, type ElementType, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'

import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'

export interface EmptyStateProps {
  icon?: ElementType<{ className?: string }>
  title: string
  description?: string
  action?: { label: string; to: string }
  children?: ReactNode
  className?: string
  /** T6.19-BE-HOMECARD.4 - light background image (e.g. a match cover) behind the icon/title. */
  coverImageUrl?: string
}

/** Liste vide ou résultat sans données - landmarks + CTA optionnel (DESIGN §11). */
export function EmptyState({ icon: Icon, title, description, action, children, className, coverImageUrl }: EmptyStateProps) {
  const titleId = useId()

  return (
    <section
      role="status"
      aria-labelledby={titleId}
      className={cn(
        'relative overflow-hidden rounded-lg border border-dashed border-border bg-muted/30 px-6 py-10 text-center',
        className,
      )}
    >
      {coverImageUrl ? (
        <img
          src={coverImageUrl}
          alt=""
          aria-hidden
          className="absolute inset-0 size-full object-cover opacity-10"
        />
      ) : null}
      <div className="relative">
        {Icon ? <Icon className="mx-auto mb-4 size-10 text-muted-foreground" aria-hidden /> : null}
        <h2 id={titleId} className="text-lg font-semibold text-foreground">
          {title}
        </h2>
        {description ? <p className="mt-2 text-sm text-muted-foreground">{description}</p> : null}
        {children}
        {action ? (
          <div className="mt-6">
            <Button asChild variant="default" size="sm">
              <Link to={action.to}>{action.label}</Link>
            </Button>
          </div>
        ) : null}
      </div>
    </section>
  )
}
